import { Link, useLocation } from "react-router-dom";
import { Menu, Plus, LogOut, Sun, Moon, ChevronDown } from "lucide-react";
import { useState } from "react";
import { NotificationBell } from "./NotificationBell";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { Avatar } from "../ui/Avatar";
import { SpotlightCard } from "../ui/SpotlightCard";
import { Sidebar } from "./Sidebar";
import { UserDropdown } from "./UserDropdown";
import { EditProfileModal } from "./EditProfileModal";

const TITLES: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/tickets": "Tickets",
  "/tickets/new": "New Ticket",
  "/admin": "Admin",
  "/system-tracker": "System Issue Tracker",
  "/hardware-tracker": "Hardware Issue Tracker",
  "/software-tracker": "Software Issue Tracker",
  "/history": "Global History",
  "/recycle-bin": "Recycle Bin",
};

function pageTitle(pathname: string): string {
  if (pathname.startsWith("/tickets/") && pathname !== "/tickets/new")
    return "Ticket Details";
  return TITLES[pathname] || "IMPACT Pro";
}

export function Topbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);

  return (
    <header className="sticky top-0 z-20 px-4 pt-4">
      <SpotlightCard
        padded={false}
        spotlightColor="rgba(0, 86, 255, 0.25)"
        className="px-4 py-3 flex items-center gap-3 overflow-visible"
      >
        <button
          className="btn-icon hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Open navigation"
        >
          <Menu className="w-4 h-4" />
        </button>

        <h1 className="text-lg font-semibold text-white truncate">
          {pageTitle(location.pathname)}
        </h1>

        <div className="flex-1" />

        <Link to="/tickets?new=true" className="btn-primary inline-flex">
          <Plus className="w-4 h-4" />
          New Ticket
        </Link>

        <button
          onClick={toggleTheme}
          className="btn-icon"
          title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}
          aria-label="Toggle theme"
        >
          {theme === "light" ? (
            <Moon className="w-4 h-4" />
          ) : (
            <Sun className="w-4 h-4" />
          )}
        </button>

        <NotificationBell />

        <UserDropdown align="right" onEditProfile={() => setEditingProfile(true)}>
          <div className="flex items-center gap-2 pl-3 ml-1 border-l border-white/10 hover:bg-white/5 p-1.5 rounded-xl transition-colors cursor-pointer">
            <Avatar name={user?.name || "?"} url={user?.avatarUrl} size="sm" />
            <div className="leading-tight">
              <div className="text-xs text-white">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">
                {user?.role}
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </div>
        </UserDropdown>

        {/* Keeping mobile log out button separate, but adding edit profile as well? 
            Or just wrap it too. For simplicity, just wrap the mobile button if needed, 
            but the prompt focused on the main user display. Let's just wrap the mobile 
            view in UserDropdown too for consistency if we wanted, but the design 
            has a standalone logout on mobile. Let's wrap a combined icon for mobile. */}
        <div className="hidden">
          <UserDropdown align="right" onEditProfile={() => setEditingProfile(true)}>
            <button className="btn-icon" title="Menu">
              <Avatar name={user?.name || "?"} url={user?.avatarUrl} size="sm" />
            </button>
          </UserDropdown>
        </div>
      </SpotlightCard>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative" onClick={() => setMobileOpen(false)}>
            <Sidebar />
          </div>
        </div>
      )}

      <EditProfileModal open={editingProfile} onClose={() => setEditingProfile(false)} />
    </header>
  );
}
